import { useEffect, useState } from 'react'
import Modal from './Modal'
import { formatRupees } from '../lib/money'
import { dateText } from '../lib/when'
import * as api from '../lib/api'
import { useCanAct } from '../lib/access'
import { SkeletonLine } from './Skeleton'

/**
 * The two ledgers, as rows.
 *
 * One side at a time rather than side by side. Side-by-side implies the rows are
 * paired, and they are not — nothing has matched them yet, and putting row 4 of
 * their statement next to row 4 of ours would invite a reader to compare two
 * lines that have nothing to do with each other. When matching exists, this is
 * where it lands.
 *
 * Amounts show paise. This is a table someone reconciles against, and a hidden
 * 47 paise is a difference nobody can see.
 */
export default function LedgerView({ request, side: initialSide = 'CLIENT', onClose }) {
  const canAct = useCanAct()
  // Opened from a specific column, so it opens on that side — clicking "View" under
  // Our records and landing on their statement is the kind of small wrongness that
  // makes people stop trusting a screen.
  const [side, setSide] = useState(initialSide)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [pulling, setPulling] = useState(false)

  useEffect(() => {
    let alive = true
    api
      .getRequest(request._id)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e.message))
    return () => {
      alive = false
    }
  }, [request._id])

  /*
   * The retry the empty state used to point at ("press “Get our
   * records” on the row") without any button by that name existing
   * anywhere — dead copy left over from before this had a real action.
   * Refetches this modal's own data so the reason (or the rows, on
   * success) show immediately; the row behind it catches up on its own
   * next reload.
   */
  const retryPull = async () => {
    setPulling(true)
    setError(null)
    try {
      await api.pullOurRecords(request._id)
      setData(await api.getRequest(request._id))
    } catch (e) {
      setError(e.message)
    } finally {
      setPulling(false)
    }
  }

  const rows = (side === 'CLIENT' ? data?.theirs : data?.ours) ?? []
  const sideInfo = side === 'CLIENT' ? data?.request?.theirSide : data?.request?.ourSide

  return (
    <Modal
      title={`${request.clientName || request.clientCode} · ${request.periodLabel}`}
      wide
      onClose={onClose}
      footer={<button className="btn" onClick={onClose}>Close</button>}
    >
      <div className="tabs" style={{ marginBottom: 14 }}>
        <button className={`tab${side === 'CLIENT' ? ' on' : ''}`} onClick={() => setSide('CLIENT')}>
          Their statement
          <span className="count">{data?.theirs?.length ?? '—'}</span>
        </button>
        <button className={`tab${side === 'OURS' ? ' on' : ''}`} onClick={() => setSide('OURS')}>
          Our records (Gati)
          <span className="count">{data?.ours?.length ?? '—'}</span>
        </button>
      </div>

      {error && <div className="note" style={{ borderColor: 'var(--red)' }}>{error}</div>}
      {!data && !error && (
        <div className="tbl">
          <div className="thead" style={{ gridTemplateColumns: '86px 1fr 108px 108px' }}>
            <div>Date</div>
            <div>Particulars</div>
            <div style={{ textAlign: 'right' }}>Debit</div>
            <div style={{ textAlign: 'right' }}>Credit</div>
          </div>
          {Array.from({ length: 6 }, (_, i) => (
            <div className="rw" key={i}>
              <div className="rowgrid" style={{ gridTemplateColumns: '86px 1fr 108px 108px' }}>
                <SkeletonLine width="60px" size="sm" />
                <SkeletonLine width={`${55 + (i % 3) * 12}%`} />
                <SkeletonLine width="70px" size="sm" />
                <SkeletonLine width="70px" size="sm" />
              </div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <>
          <dl className="kv" style={{ marginBottom: 14 }}>
            <dt>Period</dt>
            <dd>
              {dateText(request.periodFrom)} — {dateText(request.periodTo)}
            </dd>
            <dt>Closing</dt>
            <dd>
              {sideInfo?.closingBalancePaise !== null && sideInfo?.closingBalancePaise !== undefined
                ? formatRupees(sideInfo.closingBalancePaise)
                : '—'}
            </dd>
            {side === 'CLIENT' && (
              <>
                <dt>Received</dt>
                <dd>
                  {request.theirSide?.receivedAt
                    ? `${new Date(request.theirSide.receivedAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })} · ${SOURCE[request.theirSide.source] ?? request.theirSide.source ?? 'unknown source'}`
                    : 'not received yet'}
                </dd>
              </>
            )}
          </dl>

          {rows.length === 0 ? (
            <div className="note">
              {side === 'CLIENT' ? (
                'No rows on their side yet — their statement has not arrived, or it could not be read.'
              ) : sideInfo?.lastError ? (
                <>
                  The last pull from Gati failed: <b>{sideInfo.lastError}</b>
                </>
              ) : (
                'No rows on our side yet — nothing has been pulled from Gati.'
              )}
              {side === 'OURS' && (
                <div style={{ marginTop: 10 }}>
                  {canAct && (
                  <button className="btn btn-sm" disabled={pulling} onClick={retryPull}>
                    {pulling ? 'Pulling…' : sideInfo?.lastError ? 'Retry' : 'Pull from Gati'}
                  </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="tbl">
              <div className="thead" style={{ gridTemplateColumns: '86px 1fr 108px 108px' }}>
                <div>Date</div>
                <div>Particulars</div>
                <div style={{ textAlign: 'right' }}>Debit</div>
                <div style={{ textAlign: 'right' }}>Credit</div>
              </div>
              {rows.map((r) => (
                <div className="rw" key={r._id}>
                  <div className="rowgrid" style={{ gridTemplateColumns: '86px 1fr 108px 108px', cursor: 'default' }}>
                    <span className="mono">{r.txnDate}</span>
                    <span>
                      <span className="c-name" style={{ whiteSpace: 'normal' }}>
                        {r.narration || '—'}
                      </span>
                      {r.voucherNo && <span className="c-sub">{r.voucherNo}</span>}
                    </span>
                    <span className="mono" style={{ textAlign: 'right' }}>
                      {r.debitPaise ? formatRupees(r.debitPaise, { symbol: false }) : ''}
                    </span>
                    <span className="mono" style={{ textAlign: 'right' }}>
                      {r.creditPaise ? formatRupees(r.creditPaise, { symbol: false }) : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

const SOURCE = { WA: 'over WhatsApp', EMAIL: 'by email', UPLOAD: 'uploaded by hand', ERP: 'from Gati' }
